'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { useAnalyticsPageView } from '../Analytics/AnalyticsPageView';
import { useStandardTrack } from './useStandardTrack';

export type UseNWAElementImpressionParams = {
  component: string;
  /** When set with linkCopy, fires Link Viewed instead of Element Viewed. */
  destinationUrl?: string;
  linkCopy?: string;
  sectionName?: string;
};

export type UseNWAElementImpressionResult = {
  ref: (node?: Element | null) => void;
  impressionId: string;
  /** Fire view once if not yet (call before interact on fast clicks). Queues until pageViewReady. */
  ensureViewed: () => void;
};

export function useNWAElementImpression(
  params: UseNWAElementImpressionParams,
): UseNWAElementImpressionResult {
  const { component, destinationUrl, linkCopy, sectionName } = params;
  const track = useStandardTrack();
  const { pageViewReady } = useAnalyticsPageView();
  const { ref, inView } = useInView({
    root: null,
    threshold: 0,
    rootMargin: '1px 0px 0px 0px',
    triggerOnce: true,
  });

  const impressionId = useMemo(() => crypto.randomUUID(), []);
  const hasFiredRef = useRef(false);
  const pendingEnsureRef = useRef(false);

  const fireView = useCallback(() => {
    if (hasFiredRef.current) return;
    if (!pageViewReady) {
      pendingEnsureRef.current = true;
      return;
    }
    hasFiredRef.current = true;
    pendingEnsureRef.current = false;

    if (destinationUrl && linkCopy) {
      void track('Link Viewed', {
        entity_name: component,
        destination_url: destinationUrl,
        link_copy: linkCopy,
        event_id: impressionId,
        ...(sectionName ? { section_name: sectionName } : {}),
      });
      return;
    }

    void track('Element Viewed', {
      entity_name: component,
      event_id: impressionId,
      ...(sectionName ? { section_name: sectionName } : {}),
      ...(destinationUrl ? { additional_attributes: { destination_url: destinationUrl } } : {}),
    });
  }, [track, component, impressionId, destinationUrl, linkCopy, sectionName, pageViewReady]);

  useEffect(() => {
    if (!pageViewReady) return;
    if (pendingEnsureRef.current || inView) fireView();
  }, [pageViewReady, inView, fireView]);

  return { ref, impressionId, ensureViewed: fireView };
}
